// Core
import React, {
  useState,
  useEffect,
  useMemo,
} from 'react';

// Libraries
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

// Plugins
import {
  comboUnitSensorToTagType,
  isDeviceDisconnected,
  overallStatusToTagType,
  overallStatusToText,
  sensorValueToStatusText,
} from '../../../../../../../../plugins/crusher';
import { convertPressure, convertTemperature } from '../../../../../../../../plugins/general';

// Store
import { useMeasurementStore } from '../../../../../../../../store/measurement.store';

// Types
import { EdgeDeviceOperationalStatusStatus } from '../../../../../../../../types/global';
import { Theme } from '../../../../../../../../types/theme';

// Components
import {
  Paragraph,
  ParagraphSize,
  ParagraphWeight,
} from '../../../../../../../../layout/elements/Paragraph';
import { Tag } from '../../../../../../../../layout/elements/Tag';

// Media
import ClampPressureImage from '../../../../../../../../media/images/maintenance/ComboUnit/ClampPressure.avif';
import ComboUnitImage from '../../../../../../../../media/images/maintenance/ComboUnit/ComboUnit.avif';
import FilterDirtyImage from '../../../../../../../../media/images/maintenance/ComboUnit/FilterDirty.avif';
import HydraulicLevelImage from '../../../../../../../../media/images/maintenance/ComboUnit/HydraulicLevel.avif';
import HydraulicTempImage from '../../../../../../../../media/images/maintenance/ComboUnit/HydraulicTemp.avif';
import LinerContactImage from '../../../../../../../../media/images/maintenance/ComboUnit/LinerContact.avif';
import LubricationLevelImage from '../../../../../../../../media/images/maintenance/ComboUnit/LubeLevel.avif';
import ReturnTempImage from '../../../../../../../../media/images/maintenance/ComboUnit/ReturnTemp.avif';
import TankTempImage from '../../../../../../../../media/images/maintenance/ComboUnit/TankTemp.avif';
import TrampPressureImage from '../../../../../../../../media/images/maintenance/ComboUnit/TrampPressure.avif';

// View
import {
  SensorData,
  SensorImages,
  SensorsProps,
  SensorThresholds,
} from './Sensors.types';
import {
  ComboUnitImageContainer,
  ComboUnitImageStyled,
  Container,
  ContentContainer,
  LeftPanel,
  RightPanel,
  SensorItem,
  SensorName,
  SensorStatusContainer,
  SensorStatusTitle,
  SensorValue,
} from './Sensors.style';

function Sensors({
  className,
  unitData,
  edgeStatus,
  testId,
}: SensorsProps): JSX.Element {
  // Dependencies
  const { t } = useTranslation();
  const theme: Theme = useTheme();

  /* ***********************************************************************************************
  *******************************************  STATE ***********************************************
  *********************************************************************************************** */

  const [hoveredSensor, setHoveredSensor] = useState<string | null>(null);

  /* ***********************************************************************************************
  ****************************************** DEBOUNCE **********************************************
  *********************************************************************************************** */

  // Debounced hover handler using lodash
  const debouncedHandleSensorHover: _.DebouncedFunc<(
    sensorKey: string | null
  ) => void> = _.debounce(
    (sensorKey: string | null) => {
      setHoveredSensor(sensorKey);
    },
    30, // 30ms delay to prevent rapid switching
  );

  // Debounced mouse leave handler
  const debouncedHandleSensorLeave: _.DebouncedFunc<() => void> = _.debounce(
    () => {
      setHoveredSensor(null);
    },
    50, // 50ms delay to prevent rapid switching
  );

  // Force reset hover state when mouse leaves sensor area completely
  const handleSensorAreaLeave = (): void => {
    // Cancel any pending debounced operations
    debouncedHandleSensorHover.cancel();
    debouncedHandleSensorLeave.cancel();
    // Immediately reset hover state
    setHoveredSensor(null);
  };

  /* ***********************************************************************************************
  ******************************************* METHODS **********************************************
  *********************************************************************************************** */

  // Get measurement units from store
  const { pressure: pressureUnit, temperature: temperatureUnit } = useMeasurementStore();

  // Image mapping for each sensor (memoized to keep stable reference)
  const sensorImages: SensorImages = useMemo(() => ({
    hydraulicLevel: HydraulicLevelImage,
    hydraulicTemperature: HydraulicTempImage,
    clampPressure: ClampPressureImage,
    trampPressure: TrampPressureImage,
    lubricationLevel: LubricationLevelImage,
    lubricationReturnTemp: ReturnTempImage,
    lubricationTankTemperature: TankTempImage,
    lubricationSupplyTemperature: ComboUnitImage,
    linerContact: LinerContactImage,
    filterDirty: FilterDirtyImage,
  }), []);

  // Sensor data array with conditional rendering based on enable flags
  const sensorData: SensorData[] = [
    {
      key: 'hydraulicLevel',
      name: t('view.crusher.maintenance.comboUnit.sensorStatus.hydraulicLevel'),
      value: (
        <Tag
          className="not-hover-tag"
          text={sensorValueToStatusText(unitData?.oilLevelLow)}
          type={comboUnitSensorToTagType(
            unitData?.oilLevelLow,
            edgeStatus ? edgeStatus.edgeStatus : null,
            unitData?.timestamp,
          )}
        />
      ),
    },
    {
      key: 'hydraulicTemperature',
      name: t('view.crusher.maintenance.comboUnit.sensorStatus.hydraulicTemperature'),
      value: (
        <Tag
          className="not-hover-tag"
          text={overallStatusToText(unitData?.hydraulicTankTempStatus)}
          type={overallStatusToTagType(
            unitData?.hydraulicTankTempStatus,
            edgeStatus ? edgeStatus.edgeStatus : null,
            unitData?.timestamp,
          )}
        />
      ),
    },
    {
      key: 'clampPressure',
      name: t('view.crusher.maintenance.comboUnit.sensorStatus.clampPressure'),
      value: (
        <Paragraph
          size={ParagraphSize.sm}
          weight={ParagraphWeight.medium}
        >
          {convertPressure(
            unitData?.hydraulicClampPressure,
            pressureUnit,
            0,
            true,
          )?.formattedValue}
        </Paragraph>
      ),
    },
    {
      key: 'trampPressure',
      name: t('view.crusher.maintenance.comboUnit.sensorStatus.trampPressure'),
      value: (
        <Paragraph
          size={ParagraphSize.sm}
          weight={ParagraphWeight.medium}
        >
          {convertPressure(
            unitData?.hydraulicTrampPressure,
            pressureUnit,
            0,
            true,
          )?.formattedValue}
        </Paragraph>
      ),
    },
    {
      key: 'lubricationLevel',
      name: t('view.crusher.maintenance.comboUnit.sensorStatus.lubricationLevel'),
      value: (
        <Tag
          className="not-hover-tag"
          text={sensorValueToStatusText(unitData?.lubeTankOilLevel)}
          type={comboUnitSensorToTagType(
            unitData?.lubeTankOilLevel,
            edgeStatus ? edgeStatus.edgeStatus : null,
            unitData?.timestamp,
          )}
        />
      ),
    },
    {
      key: 'lubricationReturnTemp',
      name: t('view.crusher.maintenance.comboUnit.sensorStatus.lubricationReturnTemp'),
      value: (
        <Paragraph
          size={ParagraphSize.sm}
          weight={ParagraphWeight.medium}
        >
          {convertTemperature(
            unitData?.lubricationReturnTemperature,
            temperatureUnit,
            0,
            true,
          )?.formattedValue}
        </Paragraph>
      ),
    },
    {
      key: 'lubricationTankTemperature',
      name: t('view.crusher.maintenance.comboUnit.sensorStatus.lubricationTankTemperature'),
      value: (
        <Paragraph
          size={ParagraphSize.sm}
          weight={ParagraphWeight.medium}
        >
          {convertTemperature(
            unitData?.lubricationTankTemperature,
            temperatureUnit,
            0,
            true,
          )?.formattedValue}
        </Paragraph>
      ),
    },
    // Conditional sensor based on lubeSupplyTempEnable
    ...(unitData?.lubeSupplyTempEnable ? [{
      key: 'lubricationSupplyTemperature',
      name: t('view.crusher.maintenance.comboUnit.sensorStatus.lubricationSupplyTemperature'),
      value: (
        <Paragraph
          size={ParagraphSize.sm}
          weight={ParagraphWeight.medium}
        >
          {convertTemperature(
            unitData?.lubricationSupplyTemperature,
            temperatureUnit,
            0,
            true,
          )?.formattedValue}
        </Paragraph>
      ),
    }] : []),
    {
      key: 'linerContact',
      name: t('view.crusher.maintenance.comboUnit.sensorStatus.linerContact'),
      value: (
        <Tag
          className="not-hover-tag"
          text={sensorValueToStatusText(unitData?.dInputCrLinerContact)}
          type={comboUnitSensorToTagType(
            unitData?.dInputCrLinerContact,
            edgeStatus ? edgeStatus.edgeStatus : null,
            unitData?.timestamp,
          )}
        />
      ),
    },
    {
      key: 'filterDirty',
      name: t('view.crusher.maintenance.comboUnit.sensorStatus.filterDirty'),
      value: (
        <Tag
          className="not-hover-tag"
          text={sensorValueToStatusText(unitData?.dInputLubeFilterDirty)}
          type={comboUnitSensorToTagType(
            unitData?.dInputLubeFilterDirty,
            edgeStatus ? edgeStatus.edgeStatus : null,
            unitData?.timestamp,
          )}
        />
      ),
    },
  ];

  /* ***********************************************************************************************
  ************************************** COMPONENT HANDLING ****************************************
  *********************************************************************************************** */

  // Function to render sensor items
  const renderSensorItems = () => sensorData.map((sensor: SensorData) => {
    // Check if edge device is running (same logic as other functions in crusher.ts)
    const isEdgeRunning: boolean = edgeStatus?.edgeStatus
      === EdgeDeviceOperationalStatusStatus.on && !isDeviceDisconnected(
      unitData?.timestamp,
    );

    // Determine if sensor has warning or alarm based on thresholds
    let hasAlarm = false;
    let hasWarning = false;

    // Helper function to determine text color based on sensor status
    const getTextColor = () => {
      // If edge device is disconnected and sensor has active thresholds, use neutral color
      if (!isEdgeRunning && (hasAlarm || hasWarning)) {
        return theme.color.crusherDashboard.maintenance.comboUnit.neutralTypeTextColor;
      }

      if (hasAlarm) {
        return theme.color.crusherDashboard.maintenance.comboUnit.criticalTypeTextColor;
      }
      if (hasWarning) {
        return theme.color.crusherDashboard.maintenance.comboUnit.warningTypeTextColor;
      }
      return theme.color.crusherDashboard.maintenance.comboUnit.sensors.titleColor;
    };

    // Check thresholds for sensors that have them
    if (sensor.key === SensorThresholds.clampPressure && unitData?.hydraulicClampPressure) {
      const currentValue = convertPressure(
        unitData.hydraulicClampPressure,
        pressureUnit,
        2,
      ).value;
      const lowAlarmThreshold = convertPressure(
        unitData.spClampPressureLowAlarm,
        pressureUnit,
        2,
      ).value;

      if (
        !_.isNil(currentValue)
        && !_.isNil(lowAlarmThreshold)
        && currentValue
        <= lowAlarmThreshold) {
        hasAlarm = true;
      }
    } else if (
      sensor.key === SensorThresholds.trampPressure
      && unitData?.hydraulicTrampPressure
    ) {
      const currentValue = convertPressure(
        unitData.hydraulicTrampPressure,
        pressureUnit,
        2,
      ).value;
      const lowAlarmThreshold = convertPressure(
        unitData.spTrampPressureLowAlarm,
        pressureUnit,
        2,
      ).value;

      if (
        !_.isNil(currentValue)
         && !_.isNil(lowAlarmThreshold)
         && currentValue
         <= lowAlarmThreshold
      ) {
        hasAlarm = true;
      }
    } else if (
      sensor.key === SensorThresholds.lubricationReturnTemp
      && unitData?.lubricationReturnTemperature
    ) {
      const currentValue = convertTemperature(
        unitData.lubricationReturnTemperature,
        temperatureUnit,
        2,
      ).value;
      const highAlarmThreshold = convertTemperature(
        unitData.spLubeReturnTempStopFeed,
        temperatureUnit,
        2,
      ).value;
      const highWarningThreshold = convertTemperature(
        unitData.spLubeReturnTempHighWarn,
        temperatureUnit,
        2,
      ).value;
      const lowAlarmThreshold = convertTemperature(
        unitData.spLubeReturnTempLowAlarm,
        temperatureUnit,
        2,
      ).value;
      const lowWarningThreshold = convertTemperature(
        unitData.spLubeReturnTempLowWarning,
        temperatureUnit,
        2,
      ).value;

      if (!_.isNil(currentValue) && !_.isNil(highAlarmThreshold) && !_.isNil(lowAlarmThreshold)
          && (currentValue >= highAlarmThreshold || currentValue <= lowAlarmThreshold)) {
        hasAlarm = true;
      } else if (
        !_.isNil(currentValue)
        && !_.isNil(highWarningThreshold)
        && !_.isNil(lowWarningThreshold)
        && (currentValue >= highWarningThreshold || currentValue <= lowWarningThreshold)) {
        hasWarning = true;
      }
    } else if (
      sensor.key === SensorThresholds.lubricationTankTemperature
      && unitData?.lubricationTankTemperature
    ) {
      const currentValue = convertTemperature(
        unitData.lubricationTankTemperature,
        temperatureUnit,
        2,
      ).value;
      const highAlarmThreshold = convertTemperature(
        unitData.spLubeTankTempHighAlarm,
        temperatureUnit,
        2,
      ).value;
      const lowAlarmThreshold = convertTemperature(
        unitData.spLubeTankTempLowAlarm,
        temperatureUnit,
        2,
      ).value;

      if (!_.isNil(currentValue) && !_.isNil(highAlarmThreshold) && !_.isNil(lowAlarmThreshold)
          && (currentValue >= highAlarmThreshold || currentValue <= lowAlarmThreshold)) {
        hasAlarm = true;
      }
    } else if (
      sensor.key === SensorThresholds.lubricationSupplyTemperature
      && unitData?.lubricationSupplyTemperature
    ) {
      const currentValue = convertTemperature(
        unitData.lubricationSupplyTemperature,
        temperatureUnit,
        2,
      ).value;
      const lowWarningThreshold = convertTemperature(
        unitData.spLubeSupplyTempLowWarn,
        temperatureUnit,
        2,
      ).value;

      if (
        !_.isNil(currentValue)
        && !_.isNil(lowWarningThreshold)
        && currentValue
        <= lowWarningThreshold
      ) {
        hasWarning = true;
      }
    }

    return (
      <SensorItem
        key={sensor.key}
        onMouseEnter={() => debouncedHandleSensorHover(sensor.key)}
        onMouseLeave={debouncedHandleSensorLeave}
        hasWarning={hasWarning}
        hasAlarm={hasAlarm}
        isRunning={isEdgeRunning}
      >
        <SensorName>
          <Paragraph
            size={ParagraphSize.sm}
            weight={ParagraphWeight.medium}
            color={getTextColor()}
          >
            {sensor.name}
          </Paragraph>
        </SensorName>
        <SensorValue>
          {React.cloneElement(sensor.value as React.ReactElement, {
            color: getTextColor(),
          })}
        </SensorValue>
      </SensorItem>
    );
  });

  // Cleanup debounced functions on unmount
  useEffect(() => () => {
    debouncedHandleSensorHover.cancel();
    debouncedHandleSensorLeave.cancel();
  }, [debouncedHandleSensorHover, debouncedHandleSensorLeave]);

  // Preload all sensor images on mount to avoid first-hover latency
  useEffect(() => {
    Object.values(sensorImages).forEach((url: string) => {
      const img = new Image();
      img.decoding = 'async';
      img.loading = 'eager';
      img.src = url;
    });
  }, [sensorImages]);

  return (
    <Container
      className={className}
      data-testid={testId}
    >
      <ContentContainer>
        <LeftPanel>
          <SensorStatusContainer
            onMouseLeave={handleSensorAreaLeave}
          >
            <SensorStatusTitle>
              <Paragraph
                size={ParagraphSize.sm}
                weight={ParagraphWeight.bold}
              >
                {t('view.crusher.maintenance.comboUnit.sensorStatus.title')}
              </Paragraph>
            </SensorStatusTitle>
            {renderSensorItems()}
          </SensorStatusContainer>
        </LeftPanel>

        <RightPanel>
          <ComboUnitImageContainer>
            <ComboUnitImageStyled
              src={ComboUnitImage}
              alt="Combo Unit"
              isHovered={!hoveredSensor}
            />
            {/* Layer all sensor images; toggle visibility only */}
            {Object.entries(sensorImages).map(([key, url]) => {
              if (key === 'lubricationSupplyTemperature' && !unitData?.lubeSupplyTempEnable) {
                return null;
              }
              return (
                <ComboUnitImageStyled
                  key={key}
                  src={url}
                  alt={key}
                  isHovered={hoveredSensor === key}
                />
              );
            })}
          </ComboUnitImageContainer>
        </RightPanel>
      </ContentContainer>
    </Container>
  );
}

export { Sensors };
